import React from "react";
import { useTranslation } from "react-i18next";
import { MapPin, MapPinOff } from "lucide-react";
import { Input, Select } from "../UI/FormField";
import { AddressAutocompleteField } from "../GooglePlaces/AddressAutocompleteField";
import { ToggleSwitch } from "../shipments/ToggleSwitch";
import IntakeField from "./IntakeField";
import IntakeNote from "./IntakeNote";
import IntakeLinkPanel, { type IntakeLinkAction } from "./IntakeLinkPanel";
import type { Partner } from "../../types";
import type {
  IntakeDraft,
  IntakeIssue,
  IntakeLinkKey,
  IntakeStopKey,
} from "../../types/containerOrderIntake";
import {
  getIn,
  hasCoordinates,
  issuesAt,
  fieldError,
  linkBlockPath,
  linkIdField,
} from "../../utils/intakeDraft";

interface Country {
  _id: string;
  name: string;
  code: string;
}

interface IntakeStopCardProps {
  linkKey: IntakeStopKey;
  title: string;
  /** Which Oliver block this came from, shown under the title. */
  sourceLabel: string;
  draft: IntakeDraft;
  issues: IntakeIssue[];
  countries: Country[];
  apiKey: string;
  readOnly?: boolean;
  onChange: (path: string, value: unknown) => void;
  onChangeMany: (changes: Record<string, unknown>) => void;
  onLink: (key: IntakeLinkKey, action: IntakeLinkAction) => void;
}

/**
 * One company on the order — the client or a stop — as the reviewer settles it.
 *
 * Top: how it ties to the directory (the unloading stop has no tie: it is an
 * added stop, a name and an address, as on the order form). Below: the
 * snapshot the order will carry (name, address, map location), then what
 * belongs to this visit (references, PIN, slot), which stays exactly as Oliver
 * read it whatever record is linked.
 */
const IntakeStopCard: React.FC<IntakeStopCardProps> = ({
  linkKey,
  title,
  sourceLabel,
  draft,
  issues,
  countries,
  apiKey,
  readOnly,
  onChange,
  onChangeMany,
  onLink,
}) => {
  const { t } = useTranslation("containerOrderIntake");
  const base = linkBlockPath(draft, linkKey);
  if (!base) return null;

  const block = getIn(draft, base) || {};
  const isUnloading = linkKey === "unloading";
  const link = isUnloading ? undefined : draft.links[linkKey];
  const idField = isUnloading ? null : linkIdField(linkKey);
  const own = issuesAt(issues, base);

  const isClient = linkKey === "client";
  const isTerminal = linkKey === "terminalPickup" || linkKey === "terminalDropoff";
  const isLoading = linkKey === "loadingSite";
  const used = block.enabled !== false;

  /**
   * What "Add" opens with: this company as the document gives it, so adding it
   * to the directory is a check-and-save rather than retyping the order.
   */
  const createDefaults: Partial<Partner> = {
    companyName: block.companyName || "",
    registerationCode: isClient ? block.registerationCode || "" : "",
    emailAddress: (isClient ? block.emailAddress : isLoading ? block.contactEmail : "") || "",
    phoneNumber: (isClient ? block.phoneNumber : isLoading ? block.contactPhone : "") || "",
    vatNumber: isClient ? link?.prefill?.vatNumber || "" : "",
    address: {
      street: block.address?.street || "",
      city: block.address?.city || "",
      state: block.address?.state || "",
      postalCode: block.address?.postalCode || "",
      country: block.address?.country || "",
      coordinates: hasCoordinates(block.address?.coordinates)
        ? block.address.coordinates
        : null,
    },
  } as Partial<Partner>;

  const text = (field: string, label: string, opts: { required?: boolean; type?: string } = {}) => (
    <IntakeField path={`${base}.${field}`} label={label} issues={issues} required={opts.required}>
      {(error) => (
        <Input
          size="sm"
          type={opts.type || "text"}
          value={getIn(block, field) ?? ""}
          error={error}
          disabled={readOnly}
          onChange={(e) => onChange(`${base}.${field}`, e.target.value)}
        />
      )}
    </IntakeField>
  );

  const date = (field: string, label: string) => (
    <IntakeField path={`${base}.${field}`} label={label} issues={issues}>
      {(error) => (
        <Input
          size="sm"
          type="date"
          value={getIn(block, field) ?? ""}
          error={error}
          disabled={readOnly}
          onChange={(e) => onChange(`${base}.${field}`, e.target.value || null)}
        />
      )}
    </IntakeField>
  );

  const located = hasCoordinates(block.address?.coordinates);

  const border = own.some((i) => i.severity === "blocking")
    ? "border-red-300"
    : own.length
      ? "border-amber-300"
      : "border-[#E5E3DC]";

  return (
    <div data-intake-path={base} className={`bg-white border ${border} rounded-lg p-3 space-y-3`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-[14px] font-medium text-[#0B0F0E]">{title}</div>
          <div className="text-[11px] text-[#8A9590]">{sourceLabel}</div>
        </div>
        {isTerminal && (
          <ToggleSwitch
            checked={used}
            disabled={readOnly}
            onChange={() => onChange(`${base}.enabled`, !used)}
            label={used ? t("stop.used") : t("stop.notUsed")}
          />
        )}
      </div>

      {isTerminal && !used && (
        <IntakeField path={`${base}.skipReason`} label={t("stop.skipReason")} issues={issues} required>
          {(error) => (
            <Input
              size="sm"
              value={block.skipReason ?? ""}
              error={error}
              disabled={readOnly}
              onChange={(e) => onChange(`${base}.skipReason`, e.target.value)}
            />
          )}
        </IntakeField>
      )}

      {(used || isClient) && link && idField && (
        <IntakeLinkPanel
          link={link}
          selectedId={block[idField] ?? null}
          name={block.companyName ?? ""}
          createDefaults={createDefaults}
          readOnly={readOnly}
          onAction={(action) => onLink(linkKey as IntakeLinkKey, action)}
        />
      )}

      {(used || isClient) && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {text("companyName", t("stop.companyName"), { required: true })}
            {isClient && text("registerationCode", t("stop.regCode"))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <IntakeField
              path={`${base}.address.street`}
              label={t("stop.street")}
              issues={issues}
              required={!isClient}
              className="md:col-span-3"
            >
              {(error) =>
                readOnly || !apiKey ? (
                  <Input
                    size="sm"
                    value={block.address?.street ?? ""}
                    error={error}
                    disabled={readOnly}
                    onChange={(e) => onChange(`${base}.address.street`, e.target.value)}
                  />
                ) : (
                  <AddressAutocompleteField
                    apiKey={apiKey}
                    size="sm"
                    value={block.address?.street ?? ""}
                    error={error}
                    countries={countries}
                    onChange={(value) =>
                      // Typed text no longer describes the picked place, so
                      // its location goes — the same rule as the order form.
                      onChangeMany({
                        [`${base}.address.street`]: value,
                        [`${base}.address.coordinates`]: { latitude: null, longitude: null },
                      })
                    }
                    onAddressSelect={(address) =>
                      onChangeMany({
                        [`${base}.address.street`]: (address.street || "").toUpperCase(),
                        ...(address.city ? { [`${base}.address.city`]: address.city.toUpperCase() } : {}),
                        ...(address.state ? { [`${base}.address.state`]: address.state.toUpperCase() } : {}),
                        ...(address.postalCode
                          ? { [`${base}.address.postalCode`]: address.postalCode.toUpperCase() }
                          : {}),
                        ...(address.country ? { [`${base}.address.country`]: address.country } : {}),
                        [`${base}.address.coordinates`]:
                          address.latitude != null && address.longitude != null
                            ? { latitude: address.latitude, longitude: address.longitude }
                            : { latitude: null, longitude: null },
                      })
                    }
                  />
                )
              }
            </IntakeField>
            <IntakeField path={`${base}.address.city`} label={t("stop.city")} issues={issues} required className="md:col-span-3">
              {(error) => (
                <Input
                  size="sm"
                  value={block.address?.city ?? ""}
                  error={error}
                  disabled={readOnly}
                  onChange={(e) => onChange(`${base}.address.city`, e.target.value.toUpperCase())}
                />
              )}
            </IntakeField>
            <IntakeField path={`${base}.address.state`} label={t("stop.state")} issues={issues} className="md:col-span-2">
              {(error) => (
                <Input
                  size="sm"
                  value={block.address?.state ?? ""}
                  error={error}
                  disabled={readOnly}
                  onChange={(e) => onChange(`${base}.address.state`, e.target.value.toUpperCase())}
                />
              )}
            </IntakeField>
            <IntakeField path={`${base}.address.postalCode`} label={t("stop.postalCode")} issues={issues} className="md:col-span-2">
              {(error) => (
                <Input
                  size="sm"
                  value={block.address?.postalCode ?? ""}
                  error={error}
                  disabled={readOnly}
                  onChange={(e) => onChange(`${base}.address.postalCode`, e.target.value.toUpperCase())}
                />
              )}
            </IntakeField>
            <IntakeField
              path={`${base}.address.country`}
              label={t("stop.country")}
              issues={issues}
              className="md:col-span-2"
              source={block.address?.country ? null : block.address?.countryName || block.address?.countryCode || null}
            >
              {(error) => (
                <Select
                  size="sm"
                  value={block.address?.country ?? ""}
                  error={error}
                  disabled={readOnly}
                  placeholder={t("stop.chooseCountry")}
                  options={countries.map((c) => ({ value: c._id, label: c.name }))}
                  onChange={(e) => onChange(`${base}.address.country`, e.target.value || null)}
                />
              )}
            </IntakeField>
          </div>

          {!isClient && (
            <div
              data-intake-path={`${base}.address.coordinates`}
              className={`flex items-start gap-1.5 text-[12px] ${located ? "text-green-700" : "text-red-700"}`}
            >
              {located ? (
                <MapPin className="h-3.5 w-3.5 mt-[1px] shrink-0" />
              ) : (
                <MapPinOff className="h-3.5 w-3.5 mt-[1px] shrink-0" />
              )}
              {/* Unlocated says *why* in the issue's own words — "pick it from
                  the suggestions" is the instruction, not just the state. */}
              <span>
                {located
                  ? t("stop.located", {
                      lat: block.address.coordinates.latitude.toFixed(5),
                      lng: block.address.coordinates.longitude.toFixed(5),
                    })
                  : (fieldError(issues, `${base}.address.coordinates`) ?? t("stop.notLocated"))}
              </span>
            </div>
          )}

          {isClient && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {text("emailAddress", t("stop.email"))}
              {text("phoneNumber", t("stop.phone"))}
            </div>
          )}

          {!isClient && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {text("bookingRef", t("stop.reference"))}
              {text("pin", t("stop.pin"))}
              {isLoading && date("onSiteDate", t("stop.date"))}
              {isLoading && text("onSiteTime", t("stop.time"), { type: "time" })}
              {isUnloading && date("date", t("stop.date"))}
              {isUnloading && text("time", t("stop.time"), { type: "time" })}
              {linkKey === "terminalDropoff" && date("cutOffDate", t("stop.cutOffDate"))}
              {linkKey === "terminalDropoff" && text("cutOffTime", t("stop.cutOffTime"), { type: "time" })}
            </div>
          )}

          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {text("contactName", t("stop.contactName"))}
              {text("contactPhone", t("stop.contactPhone"))}
              {text("contactEmail", t("stop.contactEmail"))}
            </div>
          )}

          {isUnloading && (
            <IntakeField path={`${base}.comment`} label={t("stop.driverNote")} issues={issues}>
              {(error) => (
                <IntakeNote
                  value={block.comment ?? ""}
                  error={error}
                  disabled={readOnly}
                  onChange={(e) => onChange(`${base}.comment`, e.target.value)}
                />
              )}
            </IntakeField>
          )}
        </>
      )}
    </div>
  );
};

export default IntakeStopCard;
